import { Injectable } from '@angular/core';
import { Actions, ofType, createEffect } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { EMPTY, of, race } from 'rxjs';
import { catchError, defaultIfEmpty, map, mergeMap, take, withLatestFrom } from 'rxjs/operators';
import { currentEncounterId, isEmptyEncounterId } from '@mona/pdms/data-access-combined';
import { isSameEncounterFilter } from '@mona/shared/utils';
import { AuditLogApi } from '../../infrastructure/audit-log.api';
import * as AuditLogActions from '../actions/audit.log.actions';
import { AuditLogState } from '../reducers/audit-log.reducer';
import { selectBackendPages, selectBackendPageSize, selectPaginationId } from '../selectors/audit-log-selector';

/**
 * AuditLogEffects : loadAuditLogs
 */
@Injectable()
export class AuditLogEffects {
    loadAuditLogs$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AuditLogActions.loadAuditLogs),
            withLatestFrom(
                this.store.select(selectBackendPages),
                this.store.select(selectBackendPageSize),
                this.store.select(selectPaginationId),
            ),
            mergeMap(([action, backendPages, backendPageSize, paginationId]) => {
                if (backendPages[action.backendPage]) {
                    return []; // No need to fetch if already cached
                }

                return race(
                    isEmptyEncounterId(this.store).pipe(
                        take(1),
                        map(() => AuditLogActions.loadAuditLogsCancelled({ reason: 'encounter-cleared' })),
                    ),
                    this.auditLogApi
                        .getAuditLogs(action.encounter, action.backendPage, backendPageSize, paginationId)
                        .pipe(
                            isSameEncounterFilter(currentEncounterId(this.store), action.encounter.id),
                            map(({ auditLogs, totalCount, hasNext, paginationId, backendPageSize }) => {
                                return AuditLogActions.loadAuditLogsSuccess({
                                    items: auditLogs,
                                    backendPage: action.backendPage,
                                    backendPageSize,
                                    totalCount,
                                    hasNext,
                                    paginationId,
                                });
                            }),
                            catchError(error => {
                                return of(AuditLogActions.loadAuditLogsFailure({ error }));
                            }),
                            defaultIfEmpty(
                                AuditLogActions.loadAuditLogsCancelled({ reason: 'encounter-changed' }) as Action,
                            ),
                        ),
                );
            }),
        ),
    );

    /**
     * Constructor
     * @param actions$
     * @param auditLogApi
     * @param store
     */
    constructor(
        private actions$: Actions,
        private auditLogApi: AuditLogApi,
        private store: Store<AuditLogState>,
    ) {}
}
